import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Card, keyframes, Stack, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";

import { Check, Dot, LockKeyhole } from "lucide-react";

import {
  IgBasePrimitives,
  IgBluePrimitives,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens";
import { IgTypography } from "@intelligems/ig-theme";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";

const pulse = keyframes`
  0% { transform: scale(0.75); }
  50% { transform: scale(1.5); }
  100% { transform: scale(0.75); }
`;

type TrialModule = {
  description: string;
  id: string;
  isIncluded: boolean;
  name: string;
};

const trialModules: TrialModule[] = [
  {
    id: "smart-content",
    description: "Test and personalize content",
    isIncluded: true,
    name: "Smart Content Platform",
  },
  {
    id: "smart-merchandising",
    description: "Boost cart value",
    isIncluded: true,
    name: "Smart Merchandising",
  },
  {
    id: "smart-shipping",
    description: "Test rates and thresholds",
    isIncluded: true,
    name: "Smart Shipping",
  },
  {
    id: "smart-checkout",
    description: "Lift order value at checkout",
    isIncluded: true,
    name: "Smart Checkout & Post-purchase",
  },
  {
    id: "analytics-and-insights",
    description: "Custom insights and recommendations for your store",
    isIncluded: true,
    name: "AI-powered Analytics & Custom Insights",
  },
  {
    id: "smart-pricing-limited",
    description: "Test different prices per product",
    isIncluded: false,
    name: "Smart Pricing Limited",
  },
];

type TrialStep = {
  description: string;
  isCurrent?: boolean;
  milestone: string;
  title: string;
};

const trialSteps: TrialStep[] = [
  {
    description: "Start building AI powered tests and personalizations today.",
    isCurrent: true, //TODO hardcoded for now
    milestone: "Today",
    title: "Free trial starts.",
  },
  {
    description:
      "We'll email you before your trial ends so you can pick your plan.",
    milestone: "Day 9",
    title: "We'll send you a heads-up.",
  },
  {
    description: "Subscribe to the modules you want to keep.",
    milestone: "Day 14",
    title: "Choose your plan.",
  },
];

const TrialSteps = () => {
  const [lineHeight, setLineHeight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const wrapperHeight =
      wrapperRef.current?.getBoundingClientRect().height ?? 0;
    const lastContainerHeight =
      lastContainerRef.current?.getBoundingClientRect().height ?? 0;

    setLineHeight(wrapperHeight - lastContainerHeight);
  }, []);

  return (
    <Stack spacing={5} sx={{ position: "relative" }} ref={wrapperRef}>
      <Box
        sx={{
          backgroundColor: IgGrayPrimitives.A600,
          top: 0,
          left: "14.5px",
          position: "absolute",
          height: lineHeight + "px",
          width: "1px",
        }}
      ></Box>
      {trialSteps.map(({ description, isCurrent, milestone, title }, index) => (
        <Stack direction="row" key={title} spacing={5}>
          <Box>
            <Stack
              alignItems={"center"}
              justifyContent={"center"}
              sx={{
                backgroundColor: isCurrent
                  ? IgGrayPrimitives.A600
                  : IgGrayPrimitives[50],
                borderRadius: "50%",
                border: "solid 1px transparent",
                borderColor: isCurrent
                  ? IgGrayPrimitives.A600
                  : IgGrayPrimitives[900],
                height: "30px",
                p: 0,
                position: "relative",
                width: "30px",
              }}
            >
              <IgTypography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                  color: isCurrent
                    ? IgGrayPrimitives[50]
                    : IgGrayPrimitives.A200,
                }}
              >
                {index + 1}
              </IgTypography>
            </Stack>
          </Box>
          <Stack spacing={0.5} ref={lastContainerRef}>
            <Box>
              <IgTypography
                variant="subtitle2"
                fontWeight={500}
                color={isCurrent ? IgBasePrimitives[50] : IgGrayPrimitives.A200}
                sx={{
                  backgroundColor: isCurrent
                    ? IgBasePrimitives[500]
                    : "transparent",
                  borderRadius: "16px",
                  paddingX: isCurrent ? "8px" : "0",
                  paddingY: "2px",
                  textTransform: "uppercase",
                }}
              >
                {milestone}
              </IgTypography>
            </Box>
            <IgTypography variant="subtitle1" fontWeight={600}>
              {title}
            </IgTypography>
            <IgTypography variant="body1" color="textSecondary">
              {description}
            </IgTypography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

type TrialModuleRowProps = {
  module: TrialModule;
};

const TrialModuleRow = ({ module }: TrialModuleRowProps) => {
  const { isIncluded, name, description } = module;

  return (
    <Stack direction="row" spacing={2}>
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        flexShrink={0}
        sx={{
          width: "20px",
          height: "20px",
          backgroundColor: module.isIncluded
            ? IgBasePrimitives[500]
            : IgGrayPrimitives[500],
          borderRadius: "50%",
        }}
      >
        {isIncluded && <Check size={14} color={IgGrayPrimitives[50]} />}
        {!isIncluded && <LockKeyhole size={14} color={IgGrayPrimitives[900]} />}
      </Stack>
      <Stack>
        <IgTypography
          variant="body1"
          fontWeight={600}
          color={isIncluded ? "text.primary" : "text.disabled"}
        >
          {name}
        </IgTypography>
        <IgTypography
          variant={"body1"}
          color={isIncluded ? "text.secondary" : "text.disabled"}
        >
          {description}
        </IgTypography>
      </Stack>
    </Stack>
  );
};

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => {
  const base = theme.igPrimitives.base;
  const barColorShift = keyframes`
    0% { background-color: ${base[400]}; }
    20% { background-color: ${base[500]}; }
    40% { background-color: ${base[600]}; }
    60% { background-color: ${base[700]}; }
    80% { background-color: ${base[500]}; }
    100% { background-color: ${base[400]}; }
  `;

  return {
    height: 15,
    borderRadius: 10,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      border: `1px solid ${theme.palette.grey[800]}`,
      backgroundColor: theme.palette.grey[200],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: base[500],
      animation: `${barColorShift} 2s ease-in-out infinite alternate`,
    },
  };
});

const primaryActionSx = {
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: 500,
  letterSpacing: "1px",
  px: 4,
  py: 3,
} as const;

const centeredStateSx = {
  padding: 14,
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
} as const;

// The trial is provisioned asynchronously; there is no real progress to report,
// so the bar fills at a steady rate over the typical setup duration and stops
// short of 100% — success unmounts this step rather than completing the bar.
const TRIAL_SETUP_DURATION_MS = 10_000;
const TRIAL_SETUP_MAX_PROGRESS = 98;

const trialLoaderSentences = [
  "Rolling out the red carpet for your store",
  "Unboxing your Smart modules",
  "Warming up the optimization engine",
  "Spinning up your A/B testing lab",
  "Teaching your store to run experiments",
  "Priming the personalization engine",
  "Lining up your first winning variant",
  "Splitting a little traffic for practice",
  "Putting the finishing touches on your trial",
  "Almost ready to start testing",
];

const TrialLoadingState = () => {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);
  const [funLoaderSentence, setFunLoaderSentence] = useState(
    () =>
      trialLoaderSentences[
        Math.floor(Math.random() * trialLoaderSentences.length)
      ]
  );

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const pct = Math.min(
        TRIAL_SETUP_MAX_PROGRESS,
        ((Date.now() - start) / TRIAL_SETUP_DURATION_MS) *
          TRIAL_SETUP_MAX_PROGRESS
      );
      setProgress(pct);
      if (pct >= TRIAL_SETUP_MAX_PROGRESS) {
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  // Swap in a different random loader line (never the current one).
  useEffect(() => {
    const id = setInterval(() => {
      setFunLoaderSentence((current) => {
        const others = trialLoaderSentences.filter((s) => s !== current);
        return others[Math.floor(Math.random() * others.length)];
      });
    }, 1500);
    return () => clearInterval(id);
  }, []);

  return (
    <Stack spacing={4} sx={centeredStateSx}>
      <IgTypography variant="h3" sx={{ fontSize: 20, fontWeight: 600 }}>
        Setting up your plan
      </IgTypography>
      <IgTypography sx={{ fontSize: 16 }} color="text.secondary">
        Give us a moment to prepare your experience.
      </IgTypography>
      <Stack spacing={2} sx={{ width: "100%", alignItems: "start" }}>
        <Box sx={{ width: "100%" }}>
          <BorderLinearProgress variant="determinate" value={progress} />
        </Box>
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              animation: `${pulse} 1s ease-in-out infinite`,
            }}
          >
            <Dot color={theme.igPrimitives.base[500]} />
          </Box>
          <IgTypography
            variant="body2"
            sx={{ fontSize: 12 }}
            color="text.secondary"
          >
            {funLoaderSentence}...
          </IgTypography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export type StartTrialStatus = "overview" | "loading";

type StartTrialSecondaryAction = {
  label: string;
  onClick: () => void;
  variant?: "text" | "outlined";
};

type StartTrialStepViewProps = {
  handleStartTrial: () => void;
  secondaryAction?: StartTrialSecondaryAction;
  isModularPricesLoading: boolean;
  status: StartTrialStatus;
  trialPriceIds: string[];
  // When true the step fills its container with no card chrome of its own —
  // used inside the onboarding dialog, whose paper already supplies the card.
  embedded?: boolean;
};

export const StartTrialStepView = ({
  handleStartTrial,
  secondaryAction,
  isModularPricesLoading,
  status,
  trialPriceIds,
  embedded = false,
}: StartTrialStepViewProps) => {
  const content =
    status === "loading" ? (
      <TrialLoadingState />
    ) : (
      <Stack>
        <Stack direction="row">
          <Stack gap={6} flex={5} sx={{ paddingX: "40px", paddingY: "64px" }}>
            <Stack gap={2}>
              <IgTypography
                variant={"h1"}
                color={"text.primary"}
                fontWeight={600}
              >
                Try Intelligems free for 14 days.
              </IgTypography>
              <IgTypography variant={"body1"} color={"text.secondary"}>
                Optimize your site end to end.
              </IgTypography>
            </Stack>
            <Stack flex={1} spacing={3}>
              <IgTypography
                variant={"body2"}
                color={"text.secondary"}
                sx={{ textTransform: "uppercase" }}
                fontWeight={500}
              >
                What's included
              </IgTypography>
              <Stack spacing={3}>
                {trialModules.map((module) => (
                  <TrialModuleRow key={module.id} module={module} />
                ))}
              </Stack>
            </Stack>
          </Stack>
          <Stack
            alignItems={"center"}
            flex={4}
            sx={{
              backgroundColor: IgBluePrimitives[50],
              borderLeft: `solid 1px ${IgGrayPrimitives[600]}`,
            }}
          >
            <Stack
              sx={{ width: "300px", paddingX: "20px", paddingY: "64px" }}
              spacing={3}
            >
              <IgTypography
                variant={"h2"}
                color={"text.primary"}
                fontWeight={600}
              >
                Trial timeline
              </IgTypography>
              <TrialSteps />
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent={"flex-end"}
          sx={{
            borderTop: `solid 1px ${IgGrayPrimitives[600]}`,
            paddingX: "24px",
            paddingY: "16px",
            gap: 2,
          }}
        >
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant ?? "text"}
              sx={
                secondaryAction.variant === "outlined"
                  ? primaryActionSx
                  : undefined
              }
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            variant="contained"
            sx={primaryActionSx}
            disabled={isModularPricesLoading || trialPriceIds.length === 0}
            type="button"
            onClick={handleStartTrial}
          >
            Start free trial
          </Button>
        </Stack>
      </Stack>
    );

  if (embedded) {
    return content;
  }

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      height="100%"
      width="100%"
    >
      <Card variant="outlined" sx={{ borderRadius: "16px", width: "100%" }}>
        {content}
      </Card>
    </Stack>
  );
};
